import { useEffect, useMemo, useState } from 'react';
import type { DayTask, ObsDayContent, ScreenContent } from '@lma/shared';
import type {
  ClubTariff,
  HomeData,
  SelfNote,
  ObservationMarker,
  ObservationPoint,
  ObservationResult,
  PaymentTerms,
  Subscription,
} from './api';
import {
  CautionBlock,
  ChoiceButton,
  DashList,
  Highlight,
  LegalNote,
  Overline,
  PrimaryButton,
  ProgressDots,
  Scale,
  Secondary,
  SecondaryButton,
  TertiaryButton,
} from './components';
import { IconCheck, IconCross } from './icons';
import { VideoBlock } from './video';
import type { Labels } from './screens';

const label = (labels: Labels, key: string, fallback: string) => labels[key] ?? fallback;

/**
 * Универсальный экран содержания.
 *
 * Большинство экранов — это заголовок, абзацы, список и кнопки. Отдельный
 * компонент под каждый был бы копией предыдущего: код знает тип экрана,
 * а не его содержание.
 */
export function ContentScreen({
  screen,
  actions,
  children,
}: {
  screen: ScreenContent;
  /**
   * Действия экрана.
   *
   * `label` — подпись на случай, если в содержании её ещё нет. Раньше
   * подписи не было вовсе, и кнопка молча показывала женщине служебный
   * ключ вроде «program»: содержание правится отдельно от кода, и между
   * выкаткой и правкой всегда есть окно, в котором подписи не существует.
   */
  actions: {
    key: string;
    kind: 'primary' | 'secondary' | 'tertiary';
    label: string;
    onClick: () => void;
  }[];
  children?: React.ReactNode;
}) {
  return (
    <main className="screen">
      <div className="stack">
        {screen.overline ? <Overline>{screen.overline}</Overline> : null}
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
        {screen.bullets.length > 0 ? <DashList items={screen.bullets} /> : null}

        {/*
          Списки «что получаете» и мысль на выход рисуются здесь же.

          Раньше их умел только экран интенсива, и клуб, у которого ровно
          такая же структура, показывал заголовок и тишину вместо пяти
          пунктов. Разметка одна и та же — незачем держать её в двух местах.
        */}
        {screen.gains.length > 0 ? (
          <section className="offer">
            <span className="offer-title">{screen.buttons.gains_title ?? 'Вы получаете'}</span>
            <ul className="gain-list">
              {screen.gains.map((g) => {
                const [main, note] = splitNote(g);
                return (
                  <li key={g}>
                    <span className="gain-mark" aria-hidden="true">
                      <IconCheck />
                    </span>
                    <span>
                      {main}
                      {note ? <span className="gain-note">{note}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {screen.highlight ? <Highlight>{screen.highlight}</Highlight> : null}
        {children}

        {screen.closing ? (
          <section className="offer">
            {screen.buttons.closing_title ? (
              <span className="offer-title">{screen.buttons.closing_title}</span>
            ) : null}
            <p className="closing">{screen.closing}</p>
          </section>
        ) : null}

        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>
      <div className="stack actions">
        {actions.map((action) => {
          const text = screen.buttons[action.key] ?? action.label;
          if (action.kind === 'primary')
            return (
              <PrimaryButton key={action.key} onClick={action.onClick}>
                {text}
              </PrimaryButton>
            );
          if (action.kind === 'secondary')
            return (
              <SecondaryButton key={action.key} onClick={action.onClick}>
                {text}
              </SecondaryButton>
            );
          return (
            <TertiaryButton key={action.key} onClick={action.onClick}>
              {text}
            </TertiaryButton>
          );
        })}
      </div>
    </main>
  );
}

// ─── O-DAY День наблюдения ──────────────────────────────────

export interface DayAnswers {
  scales?: Record<string, number>;
  /** «Сегодня без оценки»: пропуск шкалы, а не ноль. */
  scaleSkipped?: boolean;
  choice?: string;
  choices?: string[];
  texts?: string[];
  measures?: Record<string, number>;
  /** Признак серии. Заполняется только в первом дне. */
  marker?: { key: string; text: string };
}

/**
 * Черновики дней. Живут, пока открыто приложение: переход в «Мой день» их не стирает.
 * Новая серия начинается с пустых черновиков.
 */
const dayDrafts = new Map<number, DayAnswers>();

export function forgetDayDrafts(): void {
  dayDrafts.clear();
}

/** Концы шкалы беспокойства — точный текст из ТЗ. */
function scaleEnds(scaleKey: string, labels: Labels): { low: string; high: string } {
  if (scaleKey === 'marker_concern') {
    return { low: 'Совсем не беспокоит', high: 'Беспокоит максимально сильно' };
  }
  return {
    low: label(labels, 'scale_low', 'нет совсем'),
    high: label(labels, 'scale_high', 'максимально выражено'),
  };
}

/** Первая шкала без ответа. Пропущенная считается отвеченной. */
function firstOpenScale(scales: { key: string }[], answers: DayAnswers): number {
  const index = scales.findIndex(
    (s) => answers.scales?.[s.key] === undefined && !answers.scaleSkipped,
  );
  return index === -1 ? scales.length : index;
}

const LAST_DAY = 7;

/** Подпись кнопки сохранения: в первом дне и в седьмом она особая. */
function saveLabel(dayIndex: number): string {
  if (dayIndex === 1) return 'Сохранить мой маркер и день 1';
  if (dayIndex === LAST_DAY) return 'Сохранить итог и открыть мою Карту';
  return `Сохранить день ${dayIndex}`;
}

/**
 * День наблюдения. Задания идут по порядку: выбор признака (день 1),
 * шкала беспокойства, поле наблюдения. Поля после ещё не отвеченной шкалы
 * скрыты, чтобы экран не превращался в анкету.
 */
export function ObservationDay({
  day,
  labels,
  marker,
  onDone,
  onSkip,
  onWorse,
  onOpenDayLog,
}: {
  day: ObsDayContent;
  labels: Labels;
  /** Признак серии. В первом дне его ещё нет. */
  marker: ObservationMarker | null;
  onDone: (answers: DayAnswers) => void;
  onSkip: () => void;
  onWorse: () => void;
  onOpenDayLog: () => void;
}) {
  const [answers, setAnswers] = useState<DayAnswers>(() => dayDrafts.get(day.dayIndex) ?? {});

  const scalesTask = day.tasks.find((t) => t.type === 'scales');
  const scales = scalesTask?.type === 'scales' ? scalesTask.scales : [];
  const [scaleStep, setScaleStep] = useState(() => firstOpenScale(scales, answers));
  const scalesDone = scales.length === 0 || scaleStep >= scales.length;

  useEffect(() => {
    dayDrafts.set(day.dayIndex, answers);
  }, [answers, day.dayIndex]);

  const complete = useMemo(
    () => isComplete(day.tasks, answers, scalesDone),
    [day, answers, scalesDone],
  );

  // Задания идут по порядку, пока шкала не отвечена. Всё после неё ждёт.
  const rendered: React.ReactNode[] = [];
  for (const [index, task] of day.tasks.entries()) {
    if (task.type === 'scales') {
      if (scalesDone) continue;
      const scale = scales[scaleStep]!;
      const ends = scaleEnds(scale.key, labels);
      rendered.push(
        <div className="stack" key={`scale-${index}`}>
          <Secondary>
            {scaleStep + 1} из {scales.length}
          </Secondary>
          <h2>{scale.label}</h2>
          <Scale
            value={answers.scales?.[scale.key] ?? null}
            onChange={(value) => {
              setAnswers((prev) => ({
                ...prev,
                scales: { ...prev.scales, [scale.key]: value },
              }));
              // Автопереход к следующей шкале.
              setTimeout(() => setScaleStep((s) => s + 1), 180);
            }}
            lowLabel={ends.low}
            highLabel={ends.high}
          />
          <TertiaryButton
            onClick={() => {
              setAnswers((prev) => ({ ...prev, scaleSkipped: true }));
              setScaleStep((s) => s + 1);
            }}
          >
            Сегодня без оценки
          </TertiaryButton>
        </div>,
      );
      break;
    }

    rendered.push(
      <TaskBlock
        key={index}
        task={task}
        labels={labels}
        answers={answers}
        onChange={setAnswers}
        onOpenDayLog={onOpenDayLog}
      />,
    );
  }

  return (
    <main className="screen">
      <div className="stack">
        <ProgressDots current={day.dayIndex} total={7} label={`День ${day.dayIndex} из 7`} />
        <h1>{day.title}</h1>

        {marker && day.dayIndex > 1 ? (
          <div className="stack marker-line">
            <Secondary>Мой признак: {marker.label}</Secondary>
            <p>{marker.text}</p>
          </div>
        ) : null}

        {day.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        <VideoBlock
          url={day.videoUrl ?? null}
          caption={day.videoCaption ?? null}
          posterUrl={day.videoPosterUrl ?? null}
          aspect={day.videoAspect ?? null}
          screen={`O-DAY-${day.dayIndex}`}
        />

        {rendered}
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={() => onDone(answers)} disabled={!complete}>
          {saveLabel(day.dayIndex)}
        </PrimaryButton>
        <SecondaryButton onClick={onOpenDayLog}>Открыть «Мой день»</SecondaryButton>
        <SecondaryButton onClick={onSkip}>
          {label(labels, 'skip_today', 'Сегодня без наблюдения')}
        </SecondaryButton>
        <TertiaryButton onClick={onWorse}>
          {label(labels, 'got_worse', 'Стало хуже')}
        </TertiaryButton>
      </div>
    </main>
  );
}

function TaskBlock({
  task,
  labels,
  answers,
  onChange,
  onOpenDayLog,
}: {
  task: DayTask;
  labels: Labels;
  answers: DayAnswers;
  onChange: (update: (prev: DayAnswers) => DayAnswers) => void;
  onOpenDayLog: () => void;
}) {
  if (task.type === 'scales') return null;

  if (task.type === 'marker_choice') {
    const chosen = answers.marker?.key;
    return (
      <div className="stack">
        <h2>{task.question}</h2>
        <div className="stack choices">
          {task.options.map((option) => (
            <div className="stack" key={option.key}>
              <ChoiceButton
                selected={chosen === option.key}
                onClick={() =>
                  onChange((prev) => ({
                    ...prev,
                    marker: {
                      key: option.key,
                      text: prev.marker?.key === option.key ? (prev.marker?.text ?? '') : '',
                    },
                  }))
                }
              >
                {option.label}
              </ChoiceButton>
              {option.examples.length > 0 ? (
                <span className="marker-examples">
                  {option.examples.map((e) => `«${e}»`).join(' ')}
                </span>
              ) : null}
              {chosen === option.key && option.memo ? <Highlight>{option.memo}</Highlight> : null}
            </div>
          ))}
        </div>

        {answers.marker ? (
          <div className="stack">
            <h2>Мой признак на эти 7 дней</h2>
            <Secondary>{task.textHint}</Secondary>
            <textarea
              className="textarea"
              rows={2}
              value={answers.marker.text}
              onChange={(e) =>
                onChange((prev) => ({
                  ...prev,
                  marker: { key: prev.marker?.key ?? '', text: e.target.value },
                }))
              }
            />
          </div>
        ) : null}
      </div>
    );
  }

  if (task.type === 'link') {
    return <TertiaryButton onClick={onOpenDayLog}>{task.label}</TertiaryButton>;
  }

  if (task.type === 'single_choice') {
    return (
      <div className="stack">
        <h2>{task.question}</h2>
        <div className="stack choices">
          {task.options.map((option) => (
            <ChoiceButton
              key={option}
              selected={answers.choice === option}
              onClick={() => onChange((prev) => ({ ...prev, choice: option }))}
            >
              {option}
            </ChoiceButton>
          ))}
        </div>
      </div>
    );
  }

  if (task.type === 'multi_choice') {
    const selected = answers.choices ?? [];
    return (
      <div className="stack">
        <h2>{task.question}</h2>
        <div className="stack choices">
          {task.options.map((option) => (
            <ChoiceButton
              key={option}
              selected={selected.includes(option)}
              onClick={() =>
                onChange((prev) => {
                  const list = prev.choices ?? [];
                  return {
                    ...prev,
                    choices: list.includes(option)
                      ? list.filter((o) => o !== option)
                      : [...list, option],
                  };
                })
              }
            >
              {option}
            </ChoiceButton>
          ))}
        </div>
      </div>
    );
  }

  if (task.type === 'free_text') {
    return (
      <div className="stack">
        {task.questions.map((question, index) => (
          <div className="stack" key={question}>
            <h2>{question}</h2>
            <textarea
              className="textarea"
              rows={3}
              value={answers.texts?.[index] ?? ''}
              onChange={(e) =>
                onChange((prev) => {
                  const texts = [...(prev.texts ?? [])];
                  texts[index] = e.target.value;
                  return { ...prev, texts };
                })
              }
            />
          </div>
        ))}
      </div>
    );
  }

  if (task.type === 'multi_measure') {
    return (
      <div className="stack">
        {task.moments.map((moment) => (
          <div className="stack" key={moment.key}>
            <h2>{moment.label}</h2>
            <Scale
              value={answers.measures?.[moment.key] ?? null}
              onChange={(value) =>
                onChange((prev) => ({
                  ...prev,
                  measures: { ...prev.measures, [moment.key]: value },
                }))
              }
              lowLabel={label(labels, 'scale_low', 'нет совсем')}
              highLabel={label(labels, 'scale_high', 'максимально выражено')}
            />
          </div>
        ))}
      </div>
    );
  }

  return null;
}

/** Кнопка «Сделано» неактивна, пока задание дня не заполнено. */
function isComplete(tasks: DayTask[], answers: DayAnswers, scalesDone: boolean): boolean {
  for (const task of tasks) {
    if (task.type === 'scales' && !scalesDone) return false;
    if (task.type === 'marker_choice' && (!answers.marker?.key || !answers.marker.text.trim())) {
      return false;
    }
    if (task.type === 'single_choice' && !answers.choice) return false;
    if (task.type === 'multi_choice' && !(answers.choices ?? []).length) return false;
    if (task.type === 'free_text') {
      const texts = answers.texts ?? [];
      if (task.questions.some((_, i) => !(texts[i] ?? '').trim())) return false;
    }
    if (task.type === 'multi_measure') {
      const measures = answers.measures ?? {};
      if (task.moments.some((m) => measures[m.key] === undefined)) return false;
    }
  }
  return true;
}

// ─── O-RESULT Карта за 7 дней ───────────────────────────────

/**
 * Карта серии: один признак, беспокойство по дням и её собственные записи.
 *
 * Пропущенный день — разрыв линии, а не ноль. Ничего не сравнивается и не
 * объявляется улучшением: график показывает только то, что она отметила.
 */
export function ObservationResultScreen({
  result,
  onReplay,
  onOpenDayLog,
  onPassport,
  onConsult,
}: {
  result: ObservationResult;
  onReplay: () => void;
  onOpenDayLog: () => void;
  onPassport: () => void;
  onConsult: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>Моя Карта за 7 дней</h1>

        {result.legacy ? (
          <Highlight>
            Эта неделя прошла по прежней версии. Её записи сохранены, а Карта по одному признаку
            начинается со следующей недели.
          </Highlight>
        ) : null}

        {result.marker ? (
          <div className="stack marker-line">
            <Secondary>Мой признак: {result.marker.label}</Secondary>
            <p>{result.marker.text}</p>
          </div>
        ) : null}

        <ConcernChart points={result.points} />

        <p className="chart-note">
          Это наблюдение за вашим беспокойством по поводу одного признака. Сам признак описан в
          записях. Оценка может измениться, когда вы меньше осуждаете себя или иначе относитесь к
          своему запросу. Это не доказывает, что физический признак уменьшился.
        </p>

        <h2>Мои записи по дням</h2>
        <div className="stack">
          {result.points.map((p) => (
            <div className="row" key={p.dayIndex}>
              <dt>
                День {p.dayIndex} · {p.concern === null ? 'без оценки' : `${p.concern} из 10`}
              </dt>
              <dd>
                {p.dayIndex === 7 && result.ownWords ? 'Итог — ниже' : (p.note ?? 'Записи нет')}
              </dd>
            </div>
          ))}
        </div>

        {result.ownWords ? (
          <>
            <h2>Мой итог</h2>
            <p>{result.ownWords}</p>
          </>
        ) : null}
      </div>

      <div className="stack actions">
        {result.productsOpen ? (
          <>
            <p className="product-note">
              Можно продолжить самостоятельно в «14 днях» или выбрать личный разбор со мной.
            </p>
            <PrimaryButton onClick={onPassport}>Купить 14 дней</PrimaryButton>
            <SecondaryButton onClick={onConsult}>Онлайн-консультация Лены ProFace</SecondaryButton>
          </>
        ) : null}
        <SecondaryButton onClick={onOpenDayLog}>Открыть «Мой день»</SecondaryButton>
        <TertiaryButton onClick={onReplay}>Пройти семь дней снова</TertiaryButton>
      </div>
    </main>
  );
}

/**
 * Мои 7 дней: что пройдено и когда следующий день.
 *
 * Отдельный экран, а не «Мой результат»: там пробы карточек, и неделя
 * наблюдения смешивалась с ними. Показывается, пока следующий день закрыт.
 */
export function ObservationProgress({
  result,
  currentDay,
  opensAt,
  onOpenDayLog,
  onHome,
}: {
  result: ObservationResult;
  currentDay: number;
  opensAt: string | null;
  onOpenDayLog: () => void;
  onHome: () => void;
}) {
  const stateOf = (p: ObservationPoint): string => {
    if (p.status === 'done') return p.concern === null ? 'без оценки' : `${p.concern} из 10`;
    if (p.status === 'skipped') return 'пропущен';
    if (p.status === 'worse') return 'остановлен';
    if (p.dayIndex === currentDay && opensAt) return `откроется ${returnTime(opensAt)}`;
    return 'впереди';
  };

  return (
    <main className="screen">
      <div className="stack">
        <ProgressDots
          current={currentDay}
          total={7}
          label={`Пройдено дней: ${result.daysDone} из 7`}
        />
        <h1>Мои 7 дней</h1>

        {result.marker ? (
          <div className="stack marker-line">
            <Secondary>Мой признак: {result.marker.label}</Secondary>
            <p>{result.marker.text}</p>
          </div>
        ) : null}

        {opensAt ? (
          <Highlight>
            День {currentDay} откроется {returnTime(opensAt)}. Дни открываются по одному: между ними
            нужно прожить обычный день.
          </Highlight>
        ) : null}

        <div className="stack">
          {result.points.map((p) => (
            <div className="row" key={p.dayIndex}>
              <dt>
                День {p.dayIndex} · {stateOf(p)}
              </dt>
              {p.note ? <dd>{p.note}</dd> : null}
            </div>
          ))}
        </div>
      </div>

      <div className="stack actions">
        <SecondaryButton onClick={onOpenDayLog}>Открыть «Мой день»</SecondaryButton>
        <TertiaryButton onClick={onHome}>На главную</TertiaryButton>
      </div>
    </main>
  );
}

/** График беспокойства: семь позиций по 0–10. Пропуск рвёт линию. */
function ConcernChart({ points }: { points: ObservationPoint[] }) {
  const width = 300;
  const height = 160;
  const pad = 24;
  const x = (day: number) => pad + ((day - 1) / 6) * (width - pad * 2);
  const y = (value: number) => height - pad - (value / 10) * (height - pad * 2);

  const segments: string[] = [];
  let current: string[] = [];
  for (const p of points) {
    if (p.concern === null) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      continue;
    }
    current.push(`${x(p.dayIndex)},${y(p.concern)}`);
  }
  if (current.length) segments.push(current.join(' '));

  return (
    <figure className="concern-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Беспокойство по дням, от 0 до 10"
      >
        {[0, 5, 10].map((v) => (
          <g key={v}>
            <line x1={pad} x2={width - pad} y1={y(v)} y2={y(v)} className="chart-grid" />
            <text x={4} y={y(v) + 4} className="chart-tick">
              {v}
            </text>
          </g>
        ))}
        {segments.map((s) => (
          <polyline key={s} points={s} className="chart-line" fill="none" />
        ))}
        {points.map((p) =>
          p.concern === null ? null : (
            <circle
              key={p.dayIndex}
              cx={x(p.dayIndex)}
              cy={y(p.concern)}
              r={3.5}
              className="chart-dot"
            />
          ),
        )}
        {points.map((p) => (
          <text
            key={p.dayIndex}
            x={x(p.dayIndex)}
            y={height - 6}
            textAnchor="middle"
            className="chart-tick"
          >
            {p.dayIndex}
          </text>
        ))}
      </svg>
      <figcaption>Как менялось моё беспокойство о признаке</figcaption>
    </figure>
  );
}

// ─── O-ROUTE Развилка ───────────────────────────────────────

/**
 * Женщина сама выбирает утверждение, которое точнее описывает её ситуацию.
 *
 * Вариант с программой скрыт, если за семь дней хотя бы раз был отмечен
 * красный флаг — независимо от того, что она выберет. На его месте
 * объяснение, а не пустое место.
 */
export function ObservationRoute({
  screen,
  showProgram,
  onChoose,
}: {
  screen: ScreenContent;
  showProgram: boolean;
  onChoose: (key: 'new_symptom' | 'pain_or_history' | 'tolerates_practice' | 'not_ready') => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
      </div>
      <div className="stack choices">
        <ChoiceButton onClick={() => onChoose('new_symptom')}>
          {screen.buttons.new_symptom}
        </ChoiceButton>
        <ChoiceButton onClick={() => onChoose('pain_or_history')}>
          {screen.buttons.pain_or_history}
        </ChoiceButton>
        {showProgram ? (
          <ChoiceButton onClick={() => onChoose('tolerates_practice')}>
            {screen.buttons.tolerates_practice}
          </ChoiceButton>
        ) : (
          <CautionBlock>{screen.buttons.hidden_note}</CautionBlock>
        )}
        <ChoiceButton onClick={() => onChoose('not_ready')}>
          {screen.buttons.not_ready}
        </ChoiceButton>
      </div>
    </main>
  );
}

// ─── LPF-CITY Город ─────────────────────────────────────────

export function CityInput({
  screen,
  onSubmit,
}: {
  screen: ScreenContent;
  onSubmit: (city: string) => void;
}) {
  const [city, setCity] = useState('');

  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        <input
          className="input"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder={screen.buttons.placeholder ?? 'Введите город'}
          autoComplete="address-level2"
        />
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={() => onSubmit(city.trim())} disabled={city.trim().length < 2}>
          {screen.buttons.continue ?? 'Продолжить'}
        </PrimaryButton>
      </div>
    </main>
  );
}

// ─── LPF-WA Переход в WhatsApp ──────────────────────────────

export function WhatsAppHandoff({
  screen,
  template,
  link,
  onOpen,
  onBack,
}: {
  screen: ScreenContent;
  template: string;
  link: string | null;
  onOpen: () => void;
  onBack: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
        <pre className="template">{template}</pre>
        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>
      <div className="stack actions">
        <PrimaryButton
          onClick={() => {
            const url = link
              ? `${link}${link.includes('?') ? '&' : '?'}text=${encodeURIComponent(template)}`
              : null;
            if (!url) return;
            // Дальше начинается переписка, которую система уже не видит:
            // этот переход — последнее, что мы можем посчитать.
            onOpen();
            window.open(url, '_blank', 'noopener');
          }}
        >
          {screen.buttons.open_whatsapp ?? 'Перейти в WhatsApp'}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── MY-RESULT Мой результат ────────────────────────────────

/**
 * Мой результат.
 *
 * Единственный экран, ради которого женщина возвращается сама. Список дат
 * с числами результатом не является: две записи от одного числа — это «до»
 * и «после» одной пробы, и без подписей они выглядят как ошибка.
 *
 * Поэтому: сначала одна фраза о том, что происходит, потом график, потом
 * пробы парами. Шкала обратная — ноль значит «совсем не беспокоит», —
 * поэтому снижение показывается как улучшение, а рост не красным,
 * а спокойно-серым: тревожить женщину из-за одного тяжёлого дня незачем.
 */
export function MyResult({
  screen,
  measurements,
  notes,
  dayBase,
  onContinue,
  onStartOver,
}: {
  screen: ScreenContent;
  measurements: {
    context: string;
    scaleKey: string | null;
    value: number | null;
    choice: string | null;
    createdAt: string;
  }[];
  /** Заметки своими словами: то, ради чего женщина сюда возвращается. */
  notes: SelfNote[];
  /**
   * База дня за те же даты. Второй слой: «отёк упал с восьми до
   * четырёх» — это много или просто выспалась?
   */
  dayBase: Record<string, { label: string; value: string }[]>;
  onContinue: () => void;
  onStartOver: () => void;
}) {
  const scaleRows = measurements.filter((m) => m.value !== null);
  const morning = measurements.filter((m) => m.context === 'morning_check');
  const probes = useMemo(() => pairProbes(measurements), [measurements]);

  // Второй слой открывается по кнопке, а не всегда: тем, кто базу дня
  // не ведёт, эти строки только зашумят экран.
  const [withBase, setWithBase] = useState(false);
  const hasBase = Object.keys(dayBase).length > 0;

  if (measurements.length === 0) {
    return (
      <main className="screen">
        <div className="stack">
          <h1>{screen.buttons.empty_title ?? 'Здесь пока пусто'}</h1>
          <Secondary>{screen.buttons.empty_body}</Secondary>
        </div>
        <div className="stack actions">
          <PrimaryButton onClick={onStartOver}>
            {screen.buttons.start_over ?? 'Выбрать запрос'}
          </PrimaryButton>
        </div>
      </main>
    );
  }

  const values = scaleRows.map((m) => Number(m.value));
  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  const delta = last - first;

  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>

        {/* Одна фраза вместо таблицы: это первое, что должно считаться. */}
        <section className="result-lead">
          <span className="result-figure" data-tone={toneOf(delta)}>
            {delta === 0 ? 'ровно' : `${delta < 0 ? '−' : '+'}${Math.abs(delta)}`}
          </span>
          <p className="result-sentence">{summarise(values, probes.length)}</p>
        </section>

        {values.length > 1 ? (
          <>
            <ResultChart values={values} />
            <p className="scale-legend">
              <span>0 — не беспокоит</span>
              <span>10 — сильнее всего</span>
            </p>
          </>
        ) : null}

        {probes.length > 0 ? (
          <>
            <h2>{screen.buttons.probes_title ?? 'Пробы'}</h2>

            {/*
              Здесь и появляется настоящая ценность: не «аналитика
              здоровья», а что происходило с лицом и каким в этот день
              было состояние. Кнопка показывается только тем, у кого
              база дня вообще есть.
            */}
            {hasBase ? (
              <button className="link-button" onClick={() => setWithBase((v) => !v)}>
                {withBase
                  ? (screen.buttons.hide_base ?? 'Скрыть базу дня')
                  : (screen.buttons.show_base ?? 'Посмотреть вместе с базой дня')}
              </button>
            ) : null}

            <div className="probe-list">
              {probes
                .slice(-8)
                .reverse()
                .map((probe) => (
                  <div className="probe" key={probe.at}>
                    <span className="probe-date">{dayLabel(probe.at)}</span>
                    <span className="probe-pair">
                      <span className="probe-before">{probe.before}</span>
                      <span className="probe-arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="probe-after">{probe.after}</span>
                    </span>
                    <span className="probe-delta" data-tone={toneOf(probe.after - probe.before)}>
                      {probe.after === probe.before
                        ? 'без изменений'
                        : `${probe.after < probe.before ? 'легче на ' : 'тяжелее на '}${Math.abs(probe.after - probe.before)}`}
                    </span>

                    {withBase && dayBase[probe.at.slice(0, 10)] ? (
                      <dl className="probe-base">
                        {dayBase[probe.at.slice(0, 10)]!.map((row) => (
                          <div key={row.label}>
                            <dt>{row.label}</dt>
                            <dd>{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                ))}
            </div>
          </>
        ) : null}

        {/*
          Записи своими словами.

          Стоят выше утренних проверок намеренно: через месяц женщина
          возвращается сюда ради них, а не ради цифр. Числа говорят,
          что изменилось; слова — что именно.
        */}
        {notes.length > 0 ? (
          <>
            <h2>{screen.buttons.notes_title ?? 'Ваши записи'}</h2>
            <div className="note-list">
              {notes.map((note) => (
                <div className="self-note" key={note.id}>
                  <span className="self-note-date">{dayLabel(note.createdAt)}</span>
                  <p>{note.text}</p>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {morning.length > 0 ? (
          <>
            <h2>{screen.buttons.morning_title ?? 'Утренние проверки'}</h2>
            <div className="morning-list">
              {morning
                .slice(-6)
                .reverse()
                .map((m, i) => (
                  <div className="morning-row" key={i}>
                    <span className="morning-date">{dayLabel(m.createdAt)}</span>
                    <span className="morning-word">{morningLabel(m.choice)}</span>
                  </div>
                ))}
            </div>
          </>
        ) : null}

        <p className="result-note">
          {screen.legalNote ??
            'Изменение не устанавливает причину. Оно показывает, что в вашем состоянии есть подвижная часть, которая на что-то реагирует.'}
        </p>
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onContinue}>
          {screen.buttons.continue_observation ?? 'Продолжить наблюдение'}
        </PrimaryButton>
        <TertiaryButton onClick={onStartOver}>
          {screen.buttons.start_over ?? 'Начать заново'}
        </TertiaryButton>
      </div>
    </main>
  );
}

/**
 * График замеров.
 *
 * Заливка под линией и подчёркнутая последняя точка: женщина должна увидеть
 * направление за секунду, не вчитываясь в числа. Сетка на нуле, пяти
 * и десяти — чтобы масштаб читался без подписей осей.
 */
function ResultChart({ values }: { values: number[] }) {
  const w = 320;
  const h = 132;
  const pad = 10;
  const step = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;

  const x = (i: number) => pad + i * step;
  const y = (v: number) => pad + (1 - v / 10) * (h - pad * 2);

  const line = values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const area = `${pad},${h - pad} ${line} ${x(values.length - 1)},${h - pad}`;

  return (
    <svg
      className="result-chart"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Замеры от ${values[0]} до ${values[values.length - 1]} по шкале от нуля до десяти`}
    >
      {[0, 5, 10].map((line) => (
        <line key={line} x1={pad} x2={w - pad} y1={y(line)} y2={y(line)} className="chart-grid" />
      ))}
      <polygon points={area} className="chart-area" />
      <polyline points={line} className="chart-line" />
      {values.map((v, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(v)}
          r={i === values.length - 1 ? 5 : 3}
          className={i === values.length - 1 ? 'chart-dot chart-dot-last' : 'chart-dot'}
        />
      ))}
    </svg>
  );
}

/** Пары «до» и «после» одной пробы: порознь эти числа ничего не значат. */
function pairProbes(
  measurements: { context: string; value: number | null; createdAt: string }[],
): { at: string; before: number; after: number }[] {
  const probes: { at: string; before: number; after: number }[] = [];
  let pending: { at: string; before: number } | null = null;

  for (const m of measurements) {
    if (m.value === null) continue;

    if (m.context === 'card_before') {
      pending = { at: m.createdAt, before: Number(m.value) };
      continue;
    }

    if (m.context === 'card_after' && pending) {
      probes.push({ at: pending.at, before: pending.before, after: Number(m.value) });
      pending = null;
    }
  }

  return probes;
}

/** Меньше — лучше: шкала измеряет то, что беспокоит. */
function toneOf(delta: number): 'improve' | 'worsen' | 'flat' {
  if (delta < 0) return 'improve';
  if (delta > 0) return 'worsen';
  return 'flat';
}

/**
 * Фраза о том, что происходит.
 *
 * Осторожно по формулировкам: мы не обещаем результата и не ставим диагноз,
 * а говорим ровно то, что видно в числах.
 */
function summarise(values: number[], probeCount: number): string {
  if (values.length === 1) {
    return 'Это ваш первый замер. Он станет точкой отсчёта — сравнивать пока не с чем.';
  }

  const first = values[0]!;
  const last = values[values.length - 1]!;
  const delta = last - first;
  const span = probeCount > 1 ? `за ${probeCount} ${probeWord(probeCount)}` : 'с первого замера';

  if (delta < 0) {
    return `Стало легче на ${Math.abs(delta)} из 10 ${span}. Это значит, что состояние меняется и на что-то реагирует.`;
  }

  if (delta > 0) {
    return `Сейчас тяжелее на ${delta} из 10, чем в первый раз. Один день ничего не решает — важна общая линия за несколько недель.`;
  }

  return `Пока ровно ${span}. Это тоже наблюдение: значит, состояние держится стабильно.`;
}

/** Три пробы, а не «3 проб»: числительное без согласования выглядит как сбой. */
function probeWord(count: number): string {
  const last = count % 10;
  const teen = count % 100 >= 11 && count % 100 <= 14;

  if (!teen && last === 1) return 'пробу';
  if (!teen && last >= 2 && last <= 4) return 'пробы';
  return 'проб';
}

/**
 * Когда возвращаться.
 *
 * «завтра после восьми утра» женщина понимает сразу, «2026-08-31T05:00:00Z» —
 * нет. Если день открывается не завтра, называем дату словами.
 */
function returnTime(iso: string): string {
  const at = new Date(iso);
  const hours = at.toLocaleTimeString('ru', { hour: 'numeric', minute: '2-digit' });

  const today = new Date();
  const sameDay = at.toDateString() === today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (sameDay) return `сегодня в ${hours}`;
  if (at.toDateString() === tomorrow.toDateString()) return `завтра после ${hours}`;

  return `${dayLabel(iso)} после ${hours}`;
}

/** «26 августа» читается, «26.08.2026» — нет. */
function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', { day: 'numeric', month: 'long' });
}

function morningLabel(choice: string | null): string {
  const map: Record<string, string> = {
    easier: 'стало легче',
    no_change: 'без изменений',
    better_then_worse: 'сначала легче, потом хуже',
    heavier: 'стало тяжелее',
    cannot_tell: 'не могу определить',
  };
  return choice ? (map[choice] ?? choice) : '—';
}

// ─── MORNING Утренняя проверка ──────────────────────────────

export function MorningCheck({
  screen,
  onAnswer,
}: {
  screen: ScreenContent;
  onAnswer: (choice: string) => void;
}) {
  const options = ['easier', 'no_change', 'better_then_worse', 'heavier', 'cannot_tell'];

  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
      </div>
      <div className="stack choices">
        {options.map((key) => (
          <ChoiceButton key={key} onClick={() => onAnswer(key)}>
            {screen.buttons[key] ?? key}
          </ChoiceButton>
        ))}
      </div>
    </main>
  );
}

// ─── LEGAL Документ целиком ─────────────────────────────────

/**
 * Оферта и политика конфиденциальности.
 *
 * Отдельный экран, а не обычный ContentScreen: документ — это пятьсот
 * абзацев, и его надо читать, а не пролистывать к кнопке. Поэтому текст
 * идёт узкой колонкой, кнопка «Назад» закреплена снизу, а нумерованные
 * пункты не превращаются в сплошное полотно.
 */
export function LegalDocument({ screen, onBack }: { screen: ScreenContent; onBack: () => void }) {
  return (
    <main className="screen legal">
      <div className="stack">
        {screen.overline ? <span className="overline">{screen.overline}</span> : null}
        <h1>{screen.title}</h1>

        <article className="legal-body">
          {screen.body.map((paragraph, i) => (
            <p key={i} className={isHeading(paragraph) ? 'legal-heading' : undefined}>
              {paragraph}
            </p>
          ))}
        </article>
      </div>

      <div className="stack actions legal-actions">
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

/** Заголовок раздела: короткая строка заглавными или «1.2. Что-то». */
function isHeading(text: string): boolean {
  if (text.length > 90) return false;
  return text === text.toUpperCase() || /^\d+\.\s/.test(text);
}

// ─── CLUB-TARIFF Выбор тарифа ───────────────────────────────

/**
 * Сетка тарифов.
 *
 * Порядок кодов — здесь, подписи и число тем — в содержании. Число тем
 * меняется вместе с программой, и требовать ради него выкатки неправильно:
 * ровно на этом уже обожглись, когда кнопка показала женщине служебный
 * ключ вместо слова.
 *
 * Запасные подписи в коде на случай, когда содержание ещё не доехало.
 */
const CLUB_TARIFFS: { code: ClubTariff; label: string; themes: string }[] = [
  { code: 'club_1', label: 'Месяц', themes: '4 темы' },
  { code: 'club_2', label: 'Два месяца', themes: '8 тем' },
  { code: 'club_3', label: 'Три месяца', themes: '12 тем — полный цикл' },
];

/**
 * Проба стоит отдельно от сетки, а не четвёртой карточкой.
 *
 * У неё другие условия: разовый платёж, две недели, интенсив не входит.
 * Поставить её в один ряд с тарифами — значит сделать вид, что это
 * просто самый короткий срок, и женщина узнает разницу уже после оплаты.
 */
const CLUB_TRIAL: ClubTariff = 'club_trial';

export function ClubTariff({
  screen,
  prices,
  selected,
  onSelect,
  onContinue,
}: {
  screen: ScreenContent;
  prices: Record<string, { priceMinor: number; currency: string } | undefined>;
  selected: ClubTariff;
  onSelect: (code: ClubTariff) => void;
  onContinue: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        <div className="tariffs">
          {CLUB_TARIFFS.map((tariff) => {
            const price = prices[tariff.code];
            return (
              <button
                key={tariff.code}
                className={`tariff${selected === tariff.code ? ' selected' : ''}`}
                onClick={() => onSelect(tariff.code)}
                aria-pressed={selected === tariff.code}
              >
                <span className="tariff-label">{screen.buttons[tariff.code] ?? tariff.label}</span>
                <span className="tariff-price">{price ? money(price) : '—'}</span>
                <span className="tariff-note">
                  {screen.buttons[`${tariff.code}_themes`] ?? tariff.themes}
                </span>
                <span className="tariff-bonus">
                  {screen.buttons.includes_intensive ?? 'интенсив 21 день входит'}
                </span>
              </button>
            );
          })}
        </div>

        {/* Проба — тише тарифов и со своими условиями, названными сразу. */}
        {prices[CLUB_TRIAL] ? (
          <button
            className={`trial${selected === CLUB_TRIAL ? ' selected' : ''}`}
            onClick={() => onSelect(CLUB_TRIAL)}
            aria-pressed={selected === CLUB_TRIAL}
          >
            <span className="trial-head">
              <span className="trial-label">
                {screen.buttons.club_trial ?? 'Сначала попробовать'}
              </span>
              <span className="trial-price">{money(prices[CLUB_TRIAL]!)}</span>
            </span>
            <span className="trial-note">
              {screen.buttons.club_trial_note ??
                'Две недели, разовый платёж. Продления нет, интенсив не входит'}
            </span>
          </button>
        ) : null}
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onContinue}>{screen.buttons.continue ?? 'Дальше'}</PrimaryButton>
      </div>
    </main>
  );
}

// ─── CLUB-TERMS Условия списания ────────────────────────────

/**
 * Что женщина видит до оплаты подписки.
 *
 * Пункт 5.9 оферты перечисляет пять обязательных вещей: цену периода,
 * периодичность, дату следующего списания, способ отключения и последствия.
 * Пункт 5.8 требует отдельного и однозначного согласия — поэтому галочка
 * не отмечена заранее, а кнопка оплаты до неё не работает.
 *
 * Это не формальность ради юриста: женщина 45+ платит картой в чужом
 * интерфейсе и должна заранее знать, что спишут снова.
 */
export function ClubTerms({
  screen,
  terms,
  busy,
  onPay,
  onBack,
}: {
  screen: ScreenContent;
  terms: PaymentTerms;
  busy: boolean;
  onPay: () => void;
  onBack: () => void;
}) {
  const [agreed, setAgreed] = useState(false);
  const period = periodName(terms.intervalMonths);

  /*
   * У пробы нет ни периодичности, ни следующего списания.
   *
   * Тот же экран с теми же строками сказал бы ей «раз в 0 месяцев» и
   * попросил согласия на регулярное списание, которого не будет. Женщина
   * ставит здесь галочку под тем, что читает, и текст обязан совпадать
   * с тем, что произойдёт с её картой.
   */
  const oneTime = !terms.recurring;
  const accessPeriod = terms.accessDays ? dayName(terms.accessDays) : null;

  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>

        {/*
          За что идёт списание — до таблицы цифр, а не после.

          Женщина соглашается на регулярное платёж и вправе понимать,
          что она получает за второе и третье списание. Раньше экран
          показывал только суммы и даты, и ответа на этот вопрос не было
          нигде.
        */}
        {screen.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {screen.gains.length > 0 ? (
          <section className="offer">
            <span className="offer-title">
              {screen.buttons.gains_title ?? 'Что входит в доступ'}
            </span>
            <ul className="gain-list">
              {screen.gains.map((g) => {
                const [main, note] = splitNote(g);
                return (
                  <li key={g}>
                    <span className="gain-mark" aria-hidden="true">
                      <IconCheck />
                    </span>
                    <span>
                      {main}
                      {note ? <span className="gain-note">{note}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {screen.highlight ? <Highlight>{screen.highlight}</Highlight> : null}

        <dl className="terms">
          <div>
            <dt>Стоимость</dt>
            <dd>
              {money(terms)}
              {oneTime ? ' разово' : ` за ${period}`}
            </dd>
          </div>

          {oneTime ? (
            <>
              <div>
                <dt>Доступ</dt>
                <dd>{accessPeriod ?? 'до конца оплаченного срока'}</dd>
              </div>
              <div>
                <dt>Повторные списания</dt>
                <dd>Их не будет. Доступ закончится сам</dd>
              </div>
            </>
          ) : (
            <>
              <div>
                <dt>Периодичность списаний</dt>
                <dd>раз в {period}</dd>
              </div>
              <div>
                <dt>Следующее списание</dt>
                <dd>{terms.nextChargeAt ? formatDate(terms.nextChargeAt) : '—'}</dd>
              </div>
              <div>
                <dt>Как отключить</dt>
                <dd>{terms.cancelHint}</dd>
              </div>
              <div>
                <dt>Что будет после отключения</dt>
                <dd>{terms.cancelConsequence}</dd>
              </div>
            </>
          )}
        </dl>

        {terms.includes.length > 0 ? (
          <p className="highlight">В этот тариф бесплатно входит интенсив 21 день.</p>
        ) : null}

        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}

        <label className="consent">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>
            {oneTime
              ? `Я согласна на разовое списание ${money(terms)}. Доступ откроется ${accessPeriod ? `на ${accessPeriod}` : 'на оплаченный срок'} и закончится сам.`
              : `Я согласна на регулярное списание ${money(terms)} раз в ${period} до отключения подписки.`}
          </span>
        </label>
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onPay} disabled={!agreed || busy || !terms.isSellable}>
          {busy ? 'Открываем оплату…' : (screen.buttons.pay ?? 'Перейти к оплате')}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── CLUB-MINE Своя подписка ────────────────────────────────

/** Отключение должно быть таким же простым, как оформление. */
export function ClubMine({
  screen,
  subscriptions,
  busy,
  onCancel,
  onBack,
}: {
  screen: ScreenContent;
  subscriptions: Subscription[];
  busy: boolean;
  onCancel: (id: string) => void;
  onBack: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>

        {subscriptions.length === 0 ? (
          <p>Подписки на клуб сейчас нет.</p>
        ) : (
          subscriptions.map((sub) => (
            <div className="subscription" key={sub.id}>
              <dl className="terms">
                <div>
                  <dt>Тариф</dt>
                  <dd>
                    {money(sub)} за {periodName(sub.intervalMonths)}
                  </dd>
                </div>
                <div>
                  <dt>{sub.cancelAtPeriodEnd ? 'Доступ до' : 'Следующее списание'}</dt>
                  <dd>{formatDate(sub.currentPeriodEnd)}</dd>
                </div>
              </dl>

              {sub.status === 'past_due' ? (
                <p className="highlight">
                  Последнее списание не прошло. Проверьте карту — доступ пока сохраняется.
                </p>
              ) : null}

              {sub.cancelAtPeriodEnd ? (
                <p>Продление отключено. Доступ сохранится до конца оплаченного периода.</p>
              ) : (
                <TertiaryButton onClick={() => onCancel(sub.id)} disabled={busy}>
                  {screen.buttons.cancel_renewal ?? 'Отключить продление'}
                </TertiaryButton>
              )}
            </div>
          ))
        )}
      </div>

      <div className="stack actions">
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

/**
 * Пояснение уходит второй строкой, тише основного текста.
 *
 * Разделитель — перенос строки, а если его нет, то тире. Перенос надёжнее:
 * в самом заголовке тире встречается («1 неделя — понять свой отёк»),
 * и разрез по нему пришёлся бы на середину названия.
 *
 * Формат задаёт Елена в панели: просто жмёт Enter внутри пункта.
 */
function splitNote(item: string): [string, string | null] {
  const line = item.indexOf('\n');
  if (line !== -1) return [item.slice(0, line).trim(), item.slice(line + 1).trim()];

  const dash = item.indexOf(' — ');
  return dash === -1 ? [item, null] : [item.slice(0, dash), item.slice(dash + 3)];
}

function money(what: { priceMinor: number; currency: string }): string {
  const amount = what.priceMinor / 100;
  const shown = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${shown} ${what.currency === 'EUR' ? '€' : what.currency}`;
}

/** «раз в три месяца» читается, «раз в 3 мес.» — нет. */
/**
 * Название срока по-русски.
 *
 * Запасная ветка даёт «5 месяцев» и на двух ломается: «2 месяцев» — это
 * не по-русски, а стоит эта строка на экране согласия с оплатой, где
 * каждое слово читают внимательно.
 */
function periodName(months: number): string {
  if (months === 1) return 'месяц';
  if (months === 2) return 'два месяца';
  if (months === 3) return 'три месяца';
  if (months === 6) return 'полгода';
  if (months === 12) return 'год';
  return months >= 2 && months <= 4 ? `${months} месяца` : `${months} месяцев`;
}

/** «14 дней» по-русски: экран согласия читают внимательно. */
function dayName(days: number): string {
  const last = days % 10;
  const teen = days % 100 >= 11 && days % 100 <= 14;

  if (!teen && last === 1) return `${days} день`;
  if (!teen && last >= 2 && last <= 4) return `${days} дня`;
  return `${days} дней`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ─── HOME Дом ───────────────────────────────────────────────

/**
 * Домашний экран.
 *
 * Женщина заходит через неделю и должна за секунду понять три вещи:
 * где остановилась, что сделать сегодня и меняется ли что-то. Раньше здесь
 * был экран «маршрут сохранён» с двумя кнопками — он не отвечал ни на один
 * из этих вопросов.
 *
 * Порядок блоков — порядок этих вопросов. Одно действие сверху, крупно:
 * если предложить три равнозначных, не будет сделано ни одного.
 */
export function Home({
  screen,
  data,
  onContinue,
  onMyResult,
  onChooseRequest,
  onAsk,
  onDay,
  dayFilled,
}: {
  screen: ScreenContent;
  data: HomeData;
  onContinue: () => void;
  onMyResult: () => void;
  onChooseRequest: () => void;
  onAsk: () => void;
  onDay: () => void;
  /** Отмечен ли сегодняшний день: приглашение меняется, а не повторяется. */
  dayFilled: boolean;
}) {
  const { observation, result, currentCard } = data;
  const waiting = observation?.waitingForNextDay === true;
  // В наблюдении, и сегодняшний день ещё можно пройти.
  const inObservation = observation !== null && observation.status === 'active' && !waiting;
  const finished = observation !== null && observation.status === 'completed';

  return (
    <main className="screen home">
      <div className="stack">
        <p className="home-greeting">
          {data.firstName ? `Здравствуйте, ${data.firstName}` : 'Здравствуйте'}
        </p>
        <h1>{screen.title}</h1>

        {/* Одно действие на сегодня. Всё остальное — ниже и тише. */}
        <section className="today">
          <p className="today-label">{todayLabel(data)}</p>
          <p className="today-what">{todayWhat(data)}</p>

          {observation && (inObservation || waiting) ? (
            <div
              className="day-track"
              role="img"
              aria-label={`Отмечено дней: ${observation.done} из ${observation.total}`}
            >
              {Array.from({ length: observation.total }, (_, i) => (
                <span key={i} className={i < observation.done ? 'day-mark done' : 'day-mark'} />
              ))}
            </div>
          ) : null}

          {/*
            Когда день уже сделан, кнопка не зовёт делать его снова — она ведёт
            к результату. Предлагать действие, которое сервер откажется принять,
            нельзя: женщина заполнит форму впустую.
          */}
          {waiting ? (
            <PrimaryButton onClick={onMyResult}>
              {screen.buttons.my_result ?? 'Посмотреть свой результат'}
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={inObservation ? onContinue : onChooseRequest}>
              {inObservation
                ? (screen.buttons.continue ?? 'Продолжить день')
                : (screen.buttons.choose_request ?? 'Выбрать запрос')}
            </PrimaryButton>
          )}
        </section>

        {currentCard ? (
          <div className="home-row">
            <span className="home-row-label">Ваш запрос</span>
            <span className="home-row-value">{currentCard.title}</span>
          </div>
        ) : null}

        {/* Маленький итог: он и отвечает на «меняется ли что-то». */}
        {result.count > 1 ? (
          <button className="result-teaser" onClick={onMyResult}>
            <span className="teaser-head">
              <span className="teaser-label">Ваш результат</span>
              <span className="teaser-figure" data-tone={toneOf(result.delta ?? 0)}>
                {result.delta === 0
                  ? 'ровно'
                  : `${(result.delta ?? 0) < 0 ? '−' : '+'}${Math.abs(result.delta ?? 0)}`}
              </span>
            </span>
            <MiniChart values={result.recent} />
            <span className="teaser-more">{screen.buttons.my_result ?? 'Посмотреть подробно'}</span>
          </button>
        ) : null}

        {/*
          База дня.

          Рядом с карточками запросов, а не в глубине меню и не
          обязательным этапом после практики. Женщина заходит сюда сама,
          раз в день, на двадцать секунд — и через три месяца у неё есть
          календарь, а не ощущение, что «вроде что-то менялось».
        */}
        <button className={`day-invite${dayFilled ? ' done' : ''}`} onClick={onDay}>
          <span className="day-invite-title">
            {dayFilled
              ? (screen.buttons.day_done ?? 'Сегодня отмечено')
              : (screen.buttons.day ?? 'База дня')}
          </span>
          <span className="day-invite-note">
            {dayFilled
              ? (screen.buttons.day_done_note ?? 'Посмотреть свой календарь')
              : (screen.buttons.day_note ?? 'Сон, движение, питание, напряжение — двадцать секунд')}
          </span>
        </button>

        {/*
          Вопрос своими словами.

          Стоит на главной, а не спрятан в меню: женщина пишет не тогда,
          когда пошла искать раздел, а когда её накрыло — «встала и устала»,
          «лицо поплыло». Если в этот момент окна нет на виду, вопроса
          не будет вовсе.
        */}
        <button className="ask-invite" onClick={onAsk}>
          <span className="ask-invite-title">
            {screen.buttons.ask ?? 'Задать вопрос своими словами'}
          </span>
          <span className="ask-invite-note">
            {screen.buttons.ask_note ?? 'Опишите, что происходит. Елена разбирает эти вопросы'}
          </span>
        </button>

        {finished ? (
          <p className="home-note">
            Семь дней пройдены. Дальше — интенсив 21 день: там мы соединяем лицо, челюсть, шею,
            грудную клетку и опору, а не повторяем одну пробу.
          </p>
        ) : null}
      </div>
    </main>
  );
}

/** Линия на карточке итога: без осей и подписей, только направление. */
function MiniChart({ values }: { values: number[] }) {
  if (values.length < 2) return null;

  const w = 260;
  const h = 44;
  const step = (w - 4) / (values.length - 1);
  const points = values.map((v, i) => `${2 + i * step},${2 + (1 - v / 10) * (h - 4)}`).join(' ');

  return (
    <svg
      className="mini-chart"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline points={points} className="mini-line" />
      <circle
        cx={2 + (values.length - 1) * step}
        cy={2 + (1 - values[values.length - 1]! / 10) * (h - 4)}
        r="3.5"
        className="mini-dot"
      />
    </svg>
  );
}

function todayLabel(data: HomeData): string {
  if (data.safetyStatus === 'stop_medical') return 'Пока на паузе';
  if (data.observation?.waitingForNextDay) return 'На сегодня всё';
  if (data.observation?.status === 'active') return 'Сегодня';
  if (data.observation?.status === 'completed') return 'Наблюдение пройдено';
  if (data.currentCard) return 'Вы остановились здесь';
  return 'С чего начать';
}

function todayWhat(data: HomeData): string {
  if (data.safetyStatus === 'stop_medical') {
    return 'Практики не выдаём до медицинской оценки. Вернуться можно в разделе «Безопасность».';
  }

  const obs = data.observation;

  /*
    День сделан — и это хорошая новость. Два задания подряд выполнять нельзя:
    организму нужно время ответить, и в этом весь смысл наблюдения.
    Раньше на этом месте была красная ошибка «Не удалось начать».
  */
  if (obs?.waitingForNextDay) {
    const left = obs.total - obs.done;
    const when = obs.opensAt ? returnTime(obs.opensAt) : 'завтра';

    if (left === 0) return 'Все семь дней позади. Загляните в результат — там видно всю картину.';

    return `Сегодняшний день записан. Следующий откроется ${when} — два задания подряд делать не нужно, организму нужно время ответить.`;
  }

  if (obs?.status === 'active') {
    const left = obs.total - obs.done;
    return left === 1
      ? 'Остался последний день наблюдения.'
      : `Отмечено ${obs.done} из ${obs.total}. Сегодняшний день займёт пару минут.`;
  }

  if (obs?.status === 'completed') {
    return 'Вы прошли все семь дней и видите свою динамику целиком.';
  }

  if (data.currentCard) {
    return 'Можно продолжить с того же запроса или выбрать другой.';
  }

  return 'Выберите, что беспокоит сильнее всего. Это займёт три-пять минут.';
}

// ─── P30-01 Интенсив ────────────────────────────────────────

/**
 * Интенсив 21 день: разовая покупка, доступ навсегда.
 *
 * Три состояния, и путать их нельзя. Доступ уже есть — ведём в канал.
 * Продаётся — показываем цену и кнопку. Цена не выставлена — честно говорим
 * об этом вместо кнопки: заплатить неизвестно сколько и упереться в пустую
 * программу женщина не должна.
 *
 * Раньше экран показывал третье состояние всегда, при любых настройках.
 */
export function Intensive({
  screen,
  terms,
  hasAccess,
  channelUrl,
  busy,
  onPay,
  onBack,
}: {
  screen: ScreenContent;
  terms: PaymentTerms | null;
  hasAccess: boolean;
  channelUrl: string | null;
  busy: boolean;
  onPay: () => void;
  onBack: () => void;
}) {
  const sellable = terms?.isSellable === true;

  return (
    <main className="screen">
      <div className="stack">
        {screen.overline ? <span className="overline">{screen.overline}</span> : null}
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        {/*
          Что получает и чего не будет — два списка с разным знаком.
          Второй важнее, чем кажется: женщина 45+ уже пробовала «гонять
          жидкость», и обещание, что этого не будет, снимает главное
          возражение раньше, чем оно прозвучит.
        */}
        {screen.gains.length > 0 ? (
          <section className="offer">
            <span className="offer-title">{screen.buttons.gains_title ?? 'Вы получаете'}</span>
            <ul className="gain-list">
              {screen.gains.map((g) => {
                const [main, note] = splitNote(g);
                return (
                  <li key={g}>
                    <span className="gain-mark" aria-hidden="true">
                      <IconCheck />
                    </span>
                    <span>
                      {main}
                      {note ? <span className="gain-note">{note}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {screen.avoids.length > 0 ? (
          <section className="offer">
            <span className="offer-title">{screen.buttons.avoids_title ?? 'Чего не будет'}</span>
            <ul className="avoid-list">
              {screen.avoids.map((a) => (
                <li key={a}>
                  <span className="avoid-mark" aria-hidden="true">
                    <IconCross />
                  </span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {screen.highlight ? <p className="highlight">{screen.highlight}</p> : null}

        {screen.bullets.length > 0 ? (
          <section className="offer">
            <span className="offer-title">
              {screen.buttons.structure_title ?? 'Как устроена программа'}
            </span>
            <ol className="week-list">
              {screen.bullets.map((b) => {
                const [main, note] = splitNote(b);
                return (
                  <li key={b}>
                    <span className="week-title">{main}</span>
                    {note ? <span className="week-note">{note}</span> : null}
                  </li>
                );
              })}
            </ol>
            {screen.buttons.practical_note ? (
              <Secondary>{screen.buttons.practical_note}</Secondary>
            ) : null}
          </section>
        ) : null}

        {hasAccess ? (
          <p className="highlight">Интенсив у вас открыт. Доступ остаётся навсегда.</p>
        ) : sellable && terms ? (
          <>
            <div className="price">
              <span className="price-figure">{money(terms)}</span>
              <span className="price-note">разово, доступ навсегда</span>
            </div>
            {screen.buttons.club_hint ? <Secondary>{screen.buttons.club_hint}</Secondary> : null}
          </>
        ) : (
          <div className="block-caution">
            <span className="block-label">{screen.buttons.price_pending}</span>
            <p>{screen.buttons.not_ready_note}</p>
          </div>
        )}

        {screen.closing ? <p className="closing">{screen.closing}</p> : null}
        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>

      <div className="stack actions">
        {hasAccess ? (
          <PrimaryButton
            onClick={() => {
              if (channelUrl) window.open(channelUrl, '_blank', 'noopener');
            }}
            disabled={!channelUrl}
          >
            {screen.buttons.open_channel ?? 'Открыть канал интенсива'}
          </PrimaryButton>
        ) : sellable ? (
          <PrimaryButton onClick={onPay} disabled={busy}>
            {busy
              ? 'Открываем оплату…'
              : (screen.buttons.start_program ?? 'Оплатить и получить доступ')}
          </PrimaryButton>
        ) : null}

        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── ASK Вопрос своими словами ──────────────────────────────

/**
 * Окно для вопроса.
 *
 * Отдельный экран, а не строка в поддержке: поддержка чинит поломки,
 * а здесь женщина рассказывает, что с ней происходит — «встала и устала»,
 * «лицо поплыло», «вижу изменения и не знаю, что делать». Это разные
 * потоки, их читают с разной целью, и смешивать их значит потерять самое
 * ценное среди «не приходит уведомление».
 *
 * Имя и возраст спрашиваем прямо и объясняем зачем. Женщина 45+ не станет
 * писать, если непонятно, что из этого увидят: поэтому граница названа
 * вслух — имя и возраст, и больше ничего.
 */
export function Ask({
  screen,
  defaultName,
  busy,
  onSend,
  onBack,
}: {
  screen: ScreenContent;
  defaultName: string;
  busy: boolean;
  onSend: (input: { text: string; authorName: string; authorAge: number | null }) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [age, setAge] = useState('');
  const [text, setText] = useState('');

  const parsedAge = age === '' ? null : Number(age);
  const ageValid = parsedAge === null || (parsedAge >= 14 && parsedAge <= 99);
  const ready = name.trim().length > 0 && text.trim().length > 0 && ageValid && !busy;

  return (
    <main className="screen">
      <div className="stack">
        {screen.overline ? <span className="overline">{screen.overline}</span> : null}
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        {screen.bullets.length > 0 ? (
          <ul className="dash-list">
            {screen.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        ) : null}

        <div className="ask-form">
          {/* Имя и возраст в одну строку: вместе они читаются как подпись. */}
          <div className="ask-who">
            <label className="ask-field">
              <span>{screen.buttons.name_label ?? 'Как вас зовут'}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
            </label>
            <label className="ask-field age">
              <span>{screen.buttons.age_label ?? 'Возраст'}</span>
              <input
                inputMode="numeric"
                value={age}
                onChange={(e) => setAge(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="47"
              />
            </label>
          </div>

          <label className="ask-field">
            <span>{screen.buttons.text_label ?? 'Что происходит'}</span>
            <textarea
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={4000}
              placeholder={
                screen.buttons.text_hint ??
                'Например: встала и устала, лицо поплыло, не понимаю что делать'
              }
            />
          </label>
        </div>

        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>

      <div className="stack actions">
        <PrimaryButton
          disabled={!ready}
          onClick={() =>
            onSend({ text: text.trim(), authorName: name.trim(), authorAge: parsedAge })
          }
        >
          {busy ? 'Отправляем…' : (screen.buttons.send ?? 'Отправить вопрос')}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── CLUB-PROGRAM Календарь двенадцати тем ──────────────────

/**
 * Двенадцать тем с датами.
 *
 * Отдельным экраном, а не списком на карточке клуба: двенадцать строк
 * с датами — это уже расписание, и читают его иначе, чем обещание.
 * Рисуем той же вертикальной лентой, что и недели интенсива: видно, что
 * это последовательность, и видно, где сейчас находишься.
 */
export function ClubProgram({
  screen,
  onJoin,
  onBack,
}: {
  screen: ScreenContent;
  onJoin: () => void;
  onBack: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        {screen.overline ? <Overline>{screen.overline}</Overline> : null}
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        <section className="offer">
          <span className="offer-title">{screen.buttons.structure_title ?? 'Календарь'}</span>
          <ol className="week-list">
            {screen.bullets.map((b) => {
              const [main, note] = splitNote(b);
              return (
                <li key={b}>
                  <span className="week-title">{main}</span>
                  {note ? <span className="week-note">{note}</span> : null}
                </li>
              );
            })}
          </ol>
        </section>

        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onJoin}>
          {screen.buttons.choose_tariff ?? 'Вступить в клуб'}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}
